import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const REFRESH_EVERY_MS = 24 * 60 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dmUnread, setDmUnread] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);
  const [nicknames, setNicknames] = useState({});

  const fetchNicknames = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get('/api/nicknames', { headers: { Authorization: `Bearer ${token}` } });
      setNicknames(res.data || {});
    } catch {}
  }, []);

  const saveNickname = useCallback(async (userId, nickname) => {
    const token = localStorage.getItem('token');
    await axios.put(`/api/nicknames/${userId}`, { nickname }, { headers: { Authorization: `Bearer ${token}` } });
    setNicknames(prev => ({ ...prev, [userId]: nickname }));
  }, []);

  const deleteNickname = useCallback(async (userId) => {
    const token = localStorage.getItem('token');
    await axios.delete(`/api/nicknames/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
    setNicknames(prev => { const next = { ...prev }; delete next[userId]; return next; });
  }, []);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } });
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDmUnread = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get('/api/dm/unread', { headers: { Authorization: `Bearer ${token}` } });
      setDmUnread(res.data.count || 0);
    } catch {}
  }, []);

  const fetchNotifUnread = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get('/api/notifications/count', { headers: { Authorization: `Bearer ${token}` } });
      setNotifUnread(res.data.count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    const cached = localStorage.getItem('user');
    if (cached) {
      try { setUser(JSON.parse(cached)); } catch {}
    }
    fetchMe();
    fetchNicknames();
  }, [fetchMe, fetchNicknames]);

  // Counts are loaded once and then refreshed by realtime events (see RealtimeContext)
  const refreshCounts = useCallback(() => {
    fetchDmUnread();
    fetchNotifUnread();
  }, [fetchDmUnread, fetchNotifUnread]);

  useEffect(() => {
    if (!user?.id) return;
    refreshCounts();
    const onDmRead = () => fetchDmUnread();
    const onNotifRead = () => fetchNotifUnread();
    window.addEventListener('dm-read', onDmRead);
    window.addEventListener('notif-read', onNotifRead);
    return () => {
      window.removeEventListener('dm-read', onDmRead);
      window.removeEventListener('notif-read', onNotifRead);
    };
  }, [user?.id, refreshCounts, fetchDmUnread, fetchNotifUnread]);

  // Sliding session: a fresh token on app start and every 24 hours
  useEffect(() => {
    if (!user?.id) return;
    const refresh = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await axios.post('/api/auth/refresh', {}, { headers: { Authorization: `Bearer ${token}` } });
        localStorage.setItem('token', res.data.token);
      } catch (err) {
        if (err.response?.status === 401) fetchMe();
      }
    };
    refresh();
    const iv = setInterval(refresh, REFRESH_EVERY_MS);
    return () => clearInterval(iv);
  }, [user?.id, fetchMe]);

  const updateUser = useCallback((fields) => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...fields };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    const token = localStorage.getItem('token');
    if (token) axios.post('/api/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setDmUnread(0);
    setNotifUnread(0);
    setNicknames({});
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser: fetchMe, updateUser, refreshCounts, dmUnread, notifUnread, nicknames, saveNickname, deleteNickname }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
